import type { BadgeProps } from '@mui/material'
import { Badge, Tab as MuiTab, Typography } from '@mui/material'
import type { TabsProps as MuiTabsProps } from '@mui/material/Tabs'
import MuiTabs from '@mui/material/Tabs'
import React from 'react'
import { Link } from 'react-router-dom'

interface Tab {
  type: string | number
  url: string
  skipQueryParams?: boolean
  label: React.ReactNode
  hidden?: boolean
  badgeText?: string | number
  badgeSx?: BadgeProps['sx']
}

export type TabsProps = {
  tabs: Tab[]
  activeTab: number | string
  handleChange: MuiTabsProps['onChange']
  showBottomBorder?: boolean
} & MuiTabsProps

const Tabs: React.FC<TabsProps> = ({ tabs, activeTab, handleChange, showBottomBorder = false, sx, ...props }) => {
  const handleTabChange: MuiTabsProps['onChange'] = (event, value) => {
    const mouseEvent = event as React.MouseEvent
    if (mouseEvent.metaKey || mouseEvent.ctrlKey) return
    handleChange?.(event, value)
  }

  return (
    <MuiTabs
      variant="scrollable"
      value={activeTab}
      onChange={handleTabChange}
      indicatorColor="primary"
      textColor="primary"
      data-testid="tabs-container"
      sx={[
        showBottomBorder && { boxShadow: theme => `inset 0 -2px 0 ${theme.palette.divider}` },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...props}
    >
      {tabs
        .filter(tab => !tab.hidden)
        .map(tab => {
          const hasAbsoluteUrl = tab.url.startsWith('/')
          return (
            <MuiTab
              value={tab.type}
              {...(hasAbsoluteUrl ? { component: Link, to: tab.url } : {})}
              label={
                <Badge color="primary" badgeContent={tab.badgeText} sx={tab.badgeSx}>
                  <Typography variant="button">{tab.label}</Typography>
                </Badge>
              }
              key={tab.type}
              data-testid={'tab-' + tab.type}
            />
          )
        })}
    </MuiTabs>
  )
}

export default Tabs
